import { createContext, useContext, useEffect, useState } from 'react';
import { displayNameFrom } from './displayName.js';

// Reads the SWA client principal from /.auth/me once on mount.
//
// Deliberately NOT polled. Identity doesn't change while the tab is open, and a
// polling loop against an endpoint is exactly the pattern that made the shared
// Snowflake warehouse ~60% of IHS's bill. If a signed-out session needs picking
// up, the API returns 401 and the caller redirects to /login.

const AuthContext = createContext({
  user: null,
  status: 'loading', // 'loading' | 'authenticated' | 'anonymous' | 'error'
  reload: () => {},
});

export function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    fetch('/.auth/me')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((body) => {
        if (cancelled) return;
        const p = body?.clientPrincipal ?? null;
        if (!p) {
          setState({ user: null, status: 'anonymous' });
          return;
        }
        setState({
          status: 'authenticated',
          user: {
            id: p.userId,
            email: p.userDetails,
            // The email-derived name ("GMyers"). meStaffId in DemoContext
            // matches IT staff against it, so it stays as it was.
            name: nameFromEmail(p.userDetails),
            // What the greeting says: the real first name when Entra sent one.
            firstName: displayNameFrom(p),
            roles: Array.isArray(p.userRoles) ? p.userRoles : [],
          },
        });
      })
      .catch(() => {
        if (!cancelled) setState({ user: null, status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return (
    <AuthContext.Provider value={{ ...state, reload: () => setReloadKey((k) => k + 1) }}>
      {children}
    </AuthContext.Provider>
  );
}

// "GMyers@intermountainhs.com" -> "GMyers". Kept for staff matching only; the
// greeting uses displayName.js.
function nameFromEmail(email) {
  if (!email) return 'there';
  const local = String(email).split('@')[0];
  return local.replace(/[._]+/g, ' ').trim() || 'there';
}

export function useAuth() {
  return useContext(AuthContext);
}
